import type {
  ContentLanguage,
  TranslationJobState,
  TranslationProviderId,
  TranslationSource,
  TranslationStatusView,
} from "@plannello/shared";
import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { api } from "@/lib/api";

/* Client side of the translation endpoints (apps/server/src/routes/translation.ts). */

export type { ContentLanguage, TranslationProviderId, TranslationSource, TranslationStatusView };

/** For notes the id is the note number (`ref`). */
export type EntityKind = "item" | "comment" | "note";

const PATHS: Record<EntityKind, string> = { item: "items", comment: "comments", note: "notes" };

const A = "/api/v1";

/** Query key of one entity's translation status; the locale is the last segment. */
export const translationKey = (kind: EntityKind, id: string, locale?: ContentLanguage) =>
  locale ? (["translation", kind, id, locale] as const) : (["translation", kind, id] as const);

/**
 * While a job is queued or running the status is polled as a safety net (the live stream normally
 * delivers `translation.updated` and `translation.job` first); a job waiting for quota is checked
 * far less often.
 */
function pollInterval(data: TranslationStatusView | undefined): number | false {
  const state = data?.job?.state;
  if (state === "queued" || state === "running") return 4_000;
  if (state === "done" && data?.status === "none") return 2_000;
  if (state === "waiting_quota") return 30_000;
  return false;
}

export const entityTranslationQuery = (kind: EntityKind, id: string, locale: ContentLanguage) =>
  queryOptions({
    queryKey: translationKey(kind, id, locale),
    queryFn: ({ signal }) =>
      api.get<TranslationStatusView>(
        `${A}/${PATHS[kind]}/${encodeURIComponent(id)}/translation?locale=${locale}`,
        signal,
      ),
    staleTime: 30_000,
    refetchInterval: (q) => pollInterval(q.state.data),
  });

/** POST translate: idempotent, answers with the fresh status (a job, or an already cached translation). */
export async function requestTranslation(
  qc: QueryClient,
  kind: EntityKind,
  id: string,
  locale: ContentLanguage,
): Promise<TranslationStatusView> {
  const view = await api.post<TranslationStatusView>(`${A}/${PATHS[kind]}/${encodeURIComponent(id)}/translate`, {
    locale,
  });
  qc.setQueryData(translationKey(kind, id, locale), view);
  return view;
}

/** Refetch whatever is cached for an entity (all locales). */
export function refreshTranslation(qc: QueryClient, kind: EntityKind, id: string) {
  void qc.invalidateQueries({ queryKey: translationKey(kind, id) });
}

// ------------------------------------------------------------------------------------------- admin

export type Quota = "rpm" | "tpm" | "rpd";
export const QUOTAS: readonly Quota[] = ["rpm", "tpm", "rpd"];
export type Limits = Record<Quota, number>;

export type AdminProvider = {
  enabled: boolean;
  /** The key itself never comes back, only whether one is stored. */
  apiKeySet: boolean;
  /** False when the stored key can no longer be decrypted (the app secret changed). */
  apiKeyUsable: boolean | null;
  model: string | null;
  baseUrl: string | null;
  limits: Limits;
  max: Partial<Limits>;
  defaultLimits: Limits;
  resetTimeZone: string;
  available: boolean;
};

export type AdminTranslation = {
  enabled: boolean;
  autoTranslate: boolean;
  autoTranslateNotes: boolean;
  fallback: boolean;
  order: TranslationProviderId[];
  available: TranslationProviderId[];
  providers: Record<TranslationProviderId, AdminProvider>;
};

export type ProviderPatch = {
  enabled?: boolean;
  /** Write-only; null removes the stored key. */
  apiKey?: string | null;
  model?: string | null;
  baseUrl?: string | null;
  limits?: Partial<Limits>;
  max?: Partial<Record<Quota, number | null>>;
};

export type AdminTranslationPatch = {
  enabled?: boolean;
  autoTranslate?: boolean;
  autoTranslateNotes?: boolean;
  fallback?: boolean;
  order?: TranslationProviderId[];
  providers?: Partial<Record<TranslationProviderId, ProviderPatch>>;
};

export type QueueRow = { interactive: number; bulk: number; total: number };
export const JOB_STATES: readonly TranslationJobState[] = ["queued", "running", "waiting_quota", "done", "failed"];

export type ProviderStatus = {
  id: TranslationProviderId;
  available: boolean;
  /** The provider that translates right now (first available in the order). */
  active: boolean;
  learned: Limits;
  defaults: Limits;
  max: Partial<Limits>;
  pausedUntil: string | null;
  pauseReason: string | null;
  last429: { at: string; quota: Quota | null; reason: string; retryDelayMs: number | null } | null;
  today: { day: string; requests: number; tokens: number };
  nextReset: string;
  heldBack: Quota[];
};

export type AdminStatus = {
  enabled: boolean;
  fallback: boolean;
  queue: Record<string, QueueRow>;
  providers: ProviderStatus[];
};

export type TestResult =
  | {
      ok: boolean;
      provider: TranslationProviderId;
      model: string | null;
      input: string;
      output: string;
      problems: string[];
      usage: { inputTokens: number; outputTokens: number } | null;
      ms: number;
    }
  | { ok: false; provider: TranslationProviderId; error: { kind: string; status: number | null; message: string } };

export type BulkResult = {
  matched: number;
  queued: number;
  alreadyTranslated: number;
  sameLanguage: number;
  skipped: number;
  truncated: boolean;
};

export const adminTranslationQuery = queryOptions({
  queryKey: ["admin", "translation"],
  queryFn: ({ signal }) => api.get<AdminTranslation>(`${A}/admin/translation`, signal),
});

export const adminStatusQuery = queryOptions({
  queryKey: ["admin", "translation", "status"],
  queryFn: ({ signal }) => api.get<AdminStatus>(`${A}/admin/translation/status`, signal),
  refetchInterval: 5_000,
});
